import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal, Radio, Rate, Input, Button, message, Form } from 'antd';
import { SmileOutlined, FrownOutlined } from '@ant-design/icons';
import templateService from 'services/templateService';

const { TextArea } = Input;

/**
 * TemplateFeedbackModal - Collect user feedback on template-generated reports
 */
const TemplateFeedbackModal = ({ visible, onClose, metadata, summaryId }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      setLoading(true);

      const feedbackData = {
        summaryId,
        templateId: metadata?.templateId,
        helpful: values.helpful,
        rating: values.rating,
        comment: values.comment,
        feedbackType: 'template-report',
        metadata: {
          templateName: metadata?.templateName,
          generatedAt: metadata?.generatedAt,
          duration: metadata?.duration,
        },
      };

      await templateService.submitFeedback(feedbackData);

      message.success('Thank you for your feedback!');
      form.resetFields();
      onClose?.();
    } catch (error) {
      if (error.errorFields) {
        // Form validation error
        message.error('Please complete all required fields');
      } else {
        console.error('Feedback submission error:', error);
        message.error('Failed to submit feedback: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    onClose?.();
  };

  return (
    <StyledModal
      title="Rate This Report"
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          Submit Feedback
        </Button>,
      ]}
      width={600}
    >
      <Container>
        <IntroText>
          Your feedback helps us improve the quality of AI-generated reports.
        </IntroText>

        <Form form={form} layout="vertical">
          {/* Helpful Question */}
          <FormSection>
            <FormLabel>Was this report helpful?</FormLabel>
            <Form.Item
              name="helpful"
              rules={[{ required: true, message: 'Please select an option' }]}
            >
              <Radio.Group size="large">
                <Radio.Button value={true}>
                  <RadioContent>
                    <SmileOutlined style={{ color: '#52c41a', fontSize: 20 }} />
                    <span>Yes, it was helpful</span>
                  </RadioContent>
                </Radio.Button>
                <Radio.Button value={false}>
                  <RadioContent>
                    <FrownOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />
                    <span>No, not helpful</span>
                  </RadioContent>
                </Radio.Button>
              </Radio.Group>
            </Form.Item>
          </FormSection>

          {/* Rating */}
          <FormSection>
            <FormLabel>Overall Rating</FormLabel>
            <Form.Item
              name="rating"
              rules={[{ required: true, message: 'Please provide a rating' }]}
            >
              <Rate
                allowHalf
                style={{ fontSize: 32 }}
                character={({ index }) => (
                  <StarIcon>⭐</StarIcon>
                )}
              />
            </Form.Item>
            <HelpText>
              Rate the quality, accuracy, and usefulness of this report
            </HelpText>
          </FormSection>

          {/* Comments */}
          <FormSection>
            <FormLabel>Comments (Optional)</FormLabel>
            <Form.Item name="comment">
              <TextArea
                rows={4}
                placeholder="Tell us what you liked or how we can improve..."
                maxLength={500}
                showCount
              />
            </Form.Item>
          </FormSection>

          {/* Report Info */}
          {metadata && (
            <InfoSection>
              <InfoRow>
                <InfoLabel>Report Template:</InfoLabel>
                <InfoValue>{metadata.templateName}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Generated:</InfoLabel>
                <InfoValue>
                  {new Date(metadata.generatedAt).toLocaleString()}
                </InfoValue>
              </InfoRow>
            </InfoSection>
          )}
        </Form>
      </Container>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding: 24px;
  }
`;

const Container = styled.div``;

const IntroText = styled.p`
  font-size: 14px;
  color: #595959;
  margin-bottom: 24px;
  text-align: center;
`;

const FormSection = styled.div`
  margin-bottom: 24px;
`;

const FormLabel = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 12px;
`;

const RadioContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
`;

const StarIcon = styled.span`
  font-size: inherit;
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 8px;
`;

const InfoSection = styled.div`
  background: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  margin-top: 24px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  font-size: 13px;
  color: #8c8c8c;
  font-weight: 500;
`;

const InfoValue = styled.span`
  font-size: 13px;
  color: #262626;
`;

export default TemplateFeedbackModal;
